import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Search, X } from 'lucide-react'

import type { CharacterVoiceCatalogueEntry } from '../../../../api/client'
import { useElevenLabsVoiceCatalogue } from '../../../../hooks/useCharacterVoiceCatalogue'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'
import Modal from '../../../ui/Modal'
import { VoiceCatalogPicker } from './VoiceCatalogPicker'
import { useVoicePreview } from './useVoicePreview'
import {
  buildVoiceCatalogueLanguageOptions,
  VOICE_CATALOGUE_AGE_OPTIONS,
  VOICE_CATALOGUE_GENDER_OPTIONS,
  voiceCatalogueAccentOptionsForLanguage,
} from './voiceCatalogueFilters'

const log = createNadDebug('dw:voice-catalogue-modal')

export type VoiceCatalogueModalKind = 'sound_expert' | 'video_editor_change_voice'

export interface VoiceCatalogueModalRequest {
  kind: VoiceCatalogueModalKind
  targetLabel: string
  selectedVoiceRef?: string | null
  onApply: (voice: CharacterVoiceCatalogueEntry) => void | Promise<void>
}

interface VoiceCatalogueModalContextValue {
  openVoiceCatalogueModal: (request: VoiceCatalogueModalRequest) => void
}

const defaultContext: VoiceCatalogueModalContextValue = {
  openVoiceCatalogueModal: () => {
    log({
      event: 'open-without-provider',
      trace_id: 'voice-catalogue-modal:missing-provider',
    })
  },
}

const VoiceCatalogueModalContext =
  createContext<VoiceCatalogueModalContextValue>(defaultContext)

export function useVoiceCatalogueModal(): VoiceCatalogueModalContextValue {
  return useContext(VoiceCatalogueModalContext)
}

export function VoiceCatalogueModalProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<VoiceCatalogueModalRequest | null>(null)

  const openVoiceCatalogueModal = useCallback((nextRequest: VoiceCatalogueModalRequest) => {
    log({
      event: 'open',
      trace_id: `voice-catalogue-modal:open:${nextRequest.kind}`,
      kind: nextRequest.kind,
      target_label: nextRequest.targetLabel,
      has_selected_voice_ref: Boolean(nextRequest.selectedVoiceRef),
    })
    setRequest(nextRequest)
  }, [])

  const close = useCallback(() => {
    if (request) {
      log({
        event: 'close',
        trace_id: `voice-catalogue-modal:close:${request.kind}`,
        kind: request.kind,
      })
    }
    setRequest(null)
  }, [request])

  const value = useMemo(
    () => ({ openVoiceCatalogueModal }),
    [openVoiceCatalogueModal],
  )

  return (
    <VoiceCatalogueModalContext.Provider value={value}>
      {children}
      <VoiceCatalogueModal request={request} onClose={close} />
    </VoiceCatalogueModalContext.Provider>
  )
}

function VoiceCatalogueModal({
  request,
  onClose,
}: {
  request: VoiceCatalogueModalRequest | null
  onClose: () => void
}) {
  const { t } = useTranslation('canvas')
  const isOpen = request !== null
  const catalogue = useElevenLabsVoiceCatalogue(isOpen)
  const voicePreview = useVoicePreview()
  const stopPreview = voicePreview.stop
  const stopPreviewRef = useRef(stopPreview)
  const activeRequestRef = useRef(request)
  const [stagedVoice, setStagedVoice] = useState<CharacterVoiceCatalogueEntry | null>(null)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)
  const languageOptions = useMemo(
    () => buildVoiceCatalogueLanguageOptions(t),
    [t],
  )
  const accentOptions = useMemo(
    () => voiceCatalogueAccentOptionsForLanguage(catalogue.language),
    [catalogue.language],
  )
  const accentIsValid =
    !catalogue.accent ||
    accentOptions.some((option) => option.value === catalogue.accent)
  const accentValue = accentIsValid ? catalogue.accent : ''

  useEffect(() => {
    stopPreviewRef.current = stopPreview
  }, [stopPreview])

  useLayoutEffect(() => {
    activeRequestRef.current = request
  }, [request])

  useEffect(() => {
    if (!request) {
      setStagedVoice(null)
      setApplyError(null)
      setApplying(false)
      stopPreviewRef.current()
      return
    }
    setStagedVoice(null)
    setApplyError(null)
    setApplying(false)
  }, [request])

  useEffect(() => {
    if (!request?.selectedVoiceRef || stagedVoice) return
    const matchingVoice = catalogue.voices.find(
      (voice) => voice.voice_ref === request.selectedVoiceRef,
    )
    if (matchingVoice) setStagedVoice(matchingVoice)
  }, [catalogue.voices, request?.selectedVoiceRef, stagedVoice])

  useEffect(() => {
    if (!accentIsValid) catalogue.setAccent('')
  }, [accentIsValid, catalogue])

  const handleLanguageChange = useCallback(
    (nextLanguage: string) => {
      catalogue.setLanguage(nextLanguage)
      const nextAccentOptions =
        voiceCatalogueAccentOptionsForLanguage(nextLanguage)
      if (
        catalogue.accent &&
        !nextAccentOptions.some((option) => option.value === catalogue.accent)
      ) {
        catalogue.setAccent('')
      }
    },
    [catalogue],
  )

  const handleCancel = useCallback(() => {
    if (request) {
      log({
        event: 'cancel',
        trace_id: `voice-catalogue-modal:cancel:${request.kind}`,
        kind: request.kind,
      })
    }
    stopPreview()
    onClose()
  }, [onClose, request, stopPreview])

  const handleApply = useCallback(async () => {
    if (!request || !stagedVoice || applying) return
    const applyRequest = request
    setApplyError(null)
    setApplying(true)
    log({
      event: 'apply:start',
      trace_id: `voice-catalogue-modal:apply:${applyRequest.kind}`,
      kind: applyRequest.kind,
      voice_id: stagedVoice.voice_id,
      voice_ref: stagedVoice.voice_ref,
    })
    try {
      await applyRequest.onApply(stagedVoice)
      if (activeRequestRef.current !== applyRequest) return
      log({
        event: 'apply:success',
        trace_id: `voice-catalogue-modal:apply-ok:${applyRequest.kind}`,
        kind: applyRequest.kind,
        voice_id: stagedVoice.voice_id,
      })
      stopPreview()
      onClose()
    } catch (error) {
      if (activeRequestRef.current !== applyRequest) return
      const message =
        error instanceof Error
          ? error.message
          : t('voiceTabContent.voiceSelectionFailed', 'Voice selection failed.')
      setApplyError(message)
      log({
        event: 'apply:error',
        trace_id: `voice-catalogue-modal:apply-error:${applyRequest.kind}`,
        kind: applyRequest.kind,
        voice_id: stagedVoice.voice_id,
        error: message,
      })
    } finally {
      if (activeRequestRef.current === applyRequest) setApplying(false)
    }
  }, [applying, onClose, request, stagedVoice, stopPreview, t])

  const headerLabel =
    request?.kind === 'video_editor_change_voice'
      ? t('adjustPanel.changeVoice', 'Change voice')
      : t('soundConfigFlyover.voiceProfile')
  const overlayClassName =
    request?.kind === 'video_editor_change_voice' ? 'z-[10001]' : undefined

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="xl"
      padded={false}
      hideCloseButton
      surfaceClassName="bg-background border border-border-subtle rounded-xl shadow-2xl max-w-[1180px]"
      overlayClassName={overlayClassName}
    >
      {request ? (
        <div
          className="flex h-[min(760px,calc(100vh-3rem))] min-h-[520px] flex-col overflow-hidden"
          data-testid="voice-catalogue-modal"
        >
          <header className="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-3">
            <div className="min-w-0">
              <p className="text-xxs font-medium uppercase text-text-muted">
                {headerLabel}
              </p>
              <h2 className="truncate text-xl font-semibold text-foreground">
                {t('voiceTabContent.voiceCatalogue')}
              </h2>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="hidden max-w-xs truncate rounded-full border border-input-border bg-input-bg px-2.5 py-1 text-xs text-muted-foreground md:inline">
                {request.targetLabel}
              </span>
              <button
                type="button"
                onClick={handleCancel}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label={t('shared:modal.closeDialog', 'Close dialog')}
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          </header>

          <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[252px_minmax(0,1fr)_300px]">
            <aside className="space-y-3 overflow-y-auto border-b border-border-subtle bg-surface-0 p-3 lg:border-b-0 lg:border-r">
              <label className="flex h-9 items-center gap-2 rounded-lg border border-input-border bg-input-bg px-2 text-text-muted focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <Search className="h-4 w-4" strokeWidth={2} />
                <input
                  value={catalogue.query}
                  onChange={(event) => catalogue.setQuery(event.target.value)}
                  placeholder={t('voiceTabContent.searchVoices')}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-text-muted"
                  data-testid="voice-catalog-modal-search"
                />
              </label>

              <FilterSelect
                label={t('voiceTabContent.filterLanguage')}
                testId="voice-catalog-modal-language"
                value={catalogue.language}
                onChange={handleLanguageChange}
                options={languageOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
              />
              <FilterSelect
                label={t('voiceTabContent.filterGender')}
                testId="voice-catalog-modal-gender"
                value={catalogue.gender}
                onChange={catalogue.setGender}
                options={VOICE_CATALOGUE_GENDER_OPTIONS.map((option) => ({
                  label: t(option.labelKey),
                  value: option.value,
                }))}
              />
              <FilterSelect
                label={t('voiceTabContent.filterAge')}
                testId="voice-catalog-modal-age"
                value={catalogue.age}
                onChange={catalogue.setAge}
                options={VOICE_CATALOGUE_AGE_OPTIONS.map((option) => ({
                  label: t(option.labelKey),
                  value: option.value,
                }))}
              />
              <FilterSelect
                label={t('voiceTabContent.filterAccent')}
                testId="voice-catalog-modal-accent"
                value={accentValue}
                onChange={catalogue.setAccent}
                disabled={accentOptions.length === 0}
                options={[
                  { label: t('voiceTabContent.filterAccentAny'), value: '' },
                  ...accentOptions,
                ]}
              />
            </aside>

            <main className="min-h-0 overflow-hidden border-b border-border-subtle p-3 lg:border-b-0 lg:border-r">
              <VoiceCatalogPicker
                voices={catalogue.voices}
                query={catalogue.query}
                language={catalogue.language}
                gender={catalogue.gender}
                age={catalogue.age}
                accent={catalogue.accent}
                isLoading={catalogue.isLoading}
                error={catalogue.error}
                selectionError={applyError}
                hasMore={catalogue.hasMore}
                selectedVoiceRef={stagedVoice?.voice_ref ?? request.selectedVoiceRef ?? undefined}
                selectingVoiceRef={applying ? stagedVoice?.voice_ref ?? null : null}
                showHeading={false}
                showFilters={false}
                selectLabel={t('voiceTabContent.chooseVoice', 'Choose')}
                selectedLabel={t('voiceTabContent.selected')}
                className="flex h-full flex-col rounded-none border-0 bg-background p-0"
                resultsClassName="min-h-0 flex-1 max-h-none"
                voicePreview={voicePreview}
                onQueryChange={catalogue.setQuery}
                onLanguageChange={catalogue.setLanguage}
                onGenderChange={catalogue.setGender}
                onAgeChange={catalogue.setAge}
                onAccentChange={catalogue.setAccent}
                onLoadMore={catalogue.loadMore}
                onRetry={catalogue.refetch}
                onSelectVoice={(voice) => {
                  setApplyError(null)
                  setStagedVoice(voice)
                  log({
                    event: 'stage',
                    trace_id: `voice-catalogue-modal:stage:${request.kind}`,
                    kind: request.kind,
                    voice_id: voice.voice_id,
                    voice_ref: voice.voice_ref,
                  })
                }}
              />
            </main>

            <aside className="flex min-h-0 flex-col bg-surface-0 p-3">
              <p className="text-xxs font-medium uppercase text-text-muted">
                {t('voiceTabContent.selectedVoice', 'Selected voice')}
              </p>
              {stagedVoice ? (
                <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-lg border border-input-border bg-surface-1 p-3">
                  <p className="truncate text-lg font-semibold text-foreground">
                    {stagedVoice.name}
                  </p>
                  <p className="mt-1 text-xs font-medium text-muted-foreground">
                    {stagedVoice.provider_label}
                  </p>
                  {stagedVoice.description ? (
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {stagedVoice.description}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {[
                      stagedVoice.language,
                      stagedVoice.locale,
                      stagedVoice.use_case,
                      ...stagedVoice.descriptors.slice(0, 6),
                    ]
                      .filter(Boolean)
                      .map((label) => (
                        <span
                          key={label}
                          className="rounded-full border border-input-border bg-input-bg px-2 py-1 text-xxs font-medium text-text-muted"
                        >
                          {label}
                        </span>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex min-h-40 flex-1 items-center justify-center rounded-lg border border-dashed border-input-border bg-input-bg p-4 text-center text-xs font-medium text-text-muted">
                  {t('voiceTabContent.selectVoiceToPreview', 'Select a voice to review it.')}
                </div>
              )}
            </aside>
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-border-subtle px-4 py-3">
            <div className="min-w-0 text-xs text-muted-foreground">
              {catalogue.isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('voiceTabContent.loading')}
                </span>
              ) : (
                t('voiceTabContent.voiceCount', '{{count}} voices', {
                  count: catalogue.voices.length,
                })
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {t('voiceTabContent.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!stagedVoice || applying}
                className={cn(
                  'flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20',
                  stagedVoice && !applying
                    ? 'border-primary bg-primary text-white hover:bg-primary/90'
                    : 'cursor-not-allowed border-input-border bg-surface-1 text-text-muted',
                )}
                data-testid="voice-catalogue-modal-apply"
              >
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('voiceTabContent.applyVoice', 'Apply voice')}
              </button>
            </div>
          </footer>
        </div>
      ) : null}
    </Modal>
  )
}

function FilterSelect({
  label,
  testId,
  value,
  options,
  disabled = false,
  onChange,
}: {
  label: string
  testId: string
  value: string
  options: Array<{ label: string; value: string }>
  disabled?: boolean
  onChange: (value: string) => void
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xxs font-medium uppercase text-text-muted">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-2 text-sm font-medium text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
        data-testid={testId}
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.value || 'any'} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}
